import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calendar, CalendarClock, Check, X, Clock, MapPin, CalendarPlus, Download, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";

const ukDateTime = (utc) =>
  new Date(utc).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const ukDate = (dateStr) =>
  new Date(dateStr + "T12:00:00Z").toLocaleDateString("en-GB", { timeZone: "Europe/London", weekday: "short", day: "numeric", month: "short", year: "numeric" });

const STATUS_BADGE = {
  none: null,
  proposed: { label: "Proposed", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-100 text-emerald-700" },
  confirmed_change_proposed: { label: "Confirmed · change proposed", cls: "bg-amber-100 text-amber-700" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-200 text-zinc-600" },
};

function Badge({ status }) {
  const b = STATUS_BADGE[status];
  if (!b) return null;
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${b.cls}`}>{b.label}</span>;
}

async function downloadIcs(cid, track) {
  try {
    const r = await api.get(`/collaborations/${cid}/schedule/${track}.ics`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/calendar" }));
    const a = document.createElement("a");
    a.href = url; a.download = `nearfluence-${track}.ics`; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  } catch { toast.error("Could not download the calendar file"); }
}

export function SchedulePanel({ collabId, myRole, disabled }) {
  const [sched, setSched] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitOpen, setVisitOpen] = useState(false);
  const [deadlineOpen, setDeadlineOpen] = useState(false);
  const [vStart, setVStart] = useState("");
  const [vEnd, setVEnd] = useState("");
  const [vLoc, setVLoc] = useState("");
  const [dDate, setDDate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/collaborations/${collabId}/schedule`).then((r) => setSched(r.data)).finally(() => setLoading(false)), [collabId]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy(true); try { const r = await fn(); setSched(r.data); } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); } };

  const proposeVisit = async () => {
    if (!vStart) { toast.error("Pick a date and time"); return; }
    await act(() => api.post(`/collaborations/${collabId}/schedule/visit/propose`, { start: vStart, end: vEnd || null, location: vLoc }));
    setVisitOpen(false); setVStart(""); setVEnd(""); setVLoc(""); toast.success("Visit proposed");
  };
  const proposeDeadline = async () => {
    if (!dDate) { toast.error("Pick a date"); return; }
    await act(() => api.post(`/collaborations/${collabId}/schedule/deadline/propose`, { date: dDate }));
    setDeadlineOpen(false); setDDate(""); toast.success("Deadline proposed");
  };
  const toggleReminder = async (track, on) => {
    try { const r = await api.post(`/collaborations/${collabId}/schedule/reminders`, { track, on }); setSched(r.data); }
    catch { toast.error("Could not update reminder"); }
  };

  if (loading) return <div className="rounded-3xl border border-zinc-200 bg-white p-6"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;
  if (!sched) return null;

  const v = sched.visit, d = sched.deadline;
  const canConfirmVisit = v.pending && v.pending.proposed_by_role !== myRole;
  const canConfirmDeadline = d.pending && d.pending.proposed_by_role !== myRole;
  const remKey = (track) => `${track}_${myRole}`;
  const remOn = (track) => sched.reminders_pref?.[remKey(track)] !== false;

  return (
    <div className="rounded-3xl border border-zinc-200 bg-white p-6" data-testid="schedule-panel">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-emerald-600" />
        <h2 className="font-display text-lg font-bold text-zinc-900">Scheduling</h2>
        <span className="ml-auto text-xs text-zinc-400">Optional · UK time</span>
      </div>
      <p className="mt-1 text-sm text-zinc-500">Agree the details in chat — save a date here only if it helps. Remote or product collaborations don't need a visit.</p>

      {/* VISIT */}
      <div className="mt-5 rounded-2xl border border-zinc-100 p-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-zinc-400" />
          <span className="font-semibold text-zinc-900">Visit</span>
          <Badge status={v.status} />
        </div>

        {v.confirmed && (
          <div className="mt-3 rounded-xl bg-emerald-50/60 p-3" data-testid="visit-confirmed">
            <p className="flex items-center gap-2 text-sm font-medium text-zinc-900"><Clock className="h-4 w-4 text-emerald-600" /> {ukDateTime(v.confirmed.start_utc)} <span className="text-xs font-normal text-zinc-500">(UK time)</span></p>
            {v.confirmed.location && <p className="mt-1 text-sm text-zinc-600">{v.confirmed.location}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
              <a data-testid="visit-google" href={v.google_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"><CalendarPlus className="h-3.5 w-3.5" /> Google Calendar</a>
              <button data-testid="visit-ics" onClick={() => downloadIcs(collabId, "visit")} className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"><Download className="h-3.5 w-3.5" /> Apple / Outlook (.ics)</button>
            </div>
            <label className="mt-3 flex items-center gap-2 text-xs text-zinc-600" data-testid="visit-reminder-toggle">
              <Switch checked={remOn("visit")} onCheckedChange={(on) => toggleReminder("visit", on)} disabled={disabled} />
              <Bell className="h-3.5 w-3.5" /> Remind me ~24h before
            </label>
            <p className="mt-2 text-[11px] text-zinc-400">Exported events don't update automatically if the date changes.</p>
          </div>
        )}

        {v.pending && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="visit-pending">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">{v.confirmed ? "Change proposed" : "Proposed"} by the {v.pending.proposed_by_role}</p>
            <p className="mt-1 flex items-center gap-2 text-sm text-zinc-900"><Clock className="h-4 w-4 text-amber-600" /> {ukDateTime(v.pending.start_utc)} <span className="text-xs text-zinc-500">(UK time)</span></p>
            {v.pending.location && <p className="mt-1 text-sm text-zinc-600">{v.pending.location}</p>}
            {canConfirmVisit ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" data-testid="visit-confirm" disabled={busy || disabled} onClick={() => act(() => api.post(`/collaborations/${collabId}/schedule/visit/confirm`)).then(() => toast.success("Visit confirmed"))} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> Confirm</Button>
                <Button size="sm" variant="outline" data-testid="visit-suggest" disabled={busy || disabled} onClick={() => setVisitOpen(true)} className="rounded-full">Suggest another time</Button>
              </div>
            ) : (
              <p className="mt-2 text-xs text-zinc-500">Waiting for the other participant to confirm.</p>
            )}
          </div>
        )}

        {!disabled && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="visit-propose" disabled={busy} onClick={() => setVisitOpen(true)} className="rounded-full"><Calendar className="mr-1 h-3.5 w-3.5" /> {v.confirmed || v.pending ? "Propose new time" : "Add a visit date"}</Button>
            {(v.confirmed || v.pending) && <Button size="sm" variant="outline" data-testid="visit-cancel" disabled={busy} onClick={() => act(() => api.post(`/collaborations/${collabId}/schedule/visit/cancel`)).then(() => toast.success("Visit cancelled"))} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"><X className="mr-1 h-3.5 w-3.5" /> Cancel visit</Button>}
          </div>
        )}
      </div>

      {/* DEADLINE */}
      <div className="mt-4 rounded-2xl border border-zinc-100 p-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-zinc-400" />
          <span className="font-semibold text-zinc-900">Content deadline</span>
          <Badge status={d.status} />
        </div>

        {d.confirmed && (
          <div className="mt-3 rounded-xl bg-emerald-50/60 p-3" data-testid="deadline-confirmed">
            <p className="flex items-center gap-2 text-sm font-medium text-zinc-900"><Calendar className="h-4 w-4 text-emerald-600" /> {ukDate(d.confirmed.date)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a data-testid="deadline-google" href={d.google_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"><CalendarPlus className="h-3.5 w-3.5" /> Google Calendar</a>
              <button data-testid="deadline-ics" onClick={() => downloadIcs(collabId, "deadline")} className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50"><Download className="h-3.5 w-3.5" /> .ics</button>
            </div>
            {myRole === "creator" && (
              <label className="mt-3 flex items-center gap-2 text-xs text-zinc-600" data-testid="deadline-reminder-toggle">
                <Switch checked={remOn("deadline")} onCheckedChange={(on) => toggleReminder("deadline", on)} disabled={disabled} />
                <Bell className="h-3.5 w-3.5" /> Remind me ~24h before
              </label>
            )}
          </div>
        )}

        {d.pending && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="deadline-pending">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">{d.confirmed ? "Change proposed" : "Proposed"} by the {d.pending.proposed_by_role}</p>
            <p className="mt-1 text-sm text-zinc-900">{ukDate(d.pending.date)}</p>
            {canConfirmDeadline ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" data-testid="deadline-confirm" disabled={busy || disabled} onClick={() => act(() => api.post(`/collaborations/${collabId}/schedule/deadline/confirm`)).then(() => toast.success("Deadline confirmed"))} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> Confirm</Button>
                <Button size="sm" variant="outline" data-testid="deadline-suggest" disabled={busy || disabled} onClick={() => setDeadlineOpen(true)} className="rounded-full">Suggest another date</Button>
              </div>
            ) : (
              <p className="mt-2 text-xs text-zinc-500">Waiting for the other participant to confirm.</p>
            )}
          </div>
        )}

        {!disabled && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid="deadline-propose" disabled={busy} onClick={() => setDeadlineOpen(true)} className="rounded-full"><Calendar className="mr-1 h-3.5 w-3.5" /> {d.confirmed || d.pending ? "Propose new date" : "Add a deadline"}</Button>
            {(d.confirmed || d.pending) && <Button size="sm" variant="outline" data-testid="deadline-cancel" disabled={busy} onClick={() => act(() => api.post(`/collaborations/${collabId}/schedule/deadline/cancel`)).then(() => toast.success("Deadline cancelled"))} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"><X className="mr-1 h-3.5 w-3.5" /> Cancel</Button>}
          </div>
        )}
      </div>

      {/* Visit dialog */}
      <Dialog open={visitOpen} onOpenChange={setVisitOpen}>
        <DialogContent className="max-w-md" data-testid="visit-dialog">
          <DialogHeader><DialogTitle>Propose a visit</DialogTitle><DialogDescription>Times are in UK time (Europe/London). The other participant will confirm or suggest a change.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium text-zinc-700">Date &amp; time</label><Input type="datetime-local" data-testid="visit-start" value={vStart} onChange={(e) => setVStart(e.target.value)} className="mt-1 h-11" /></div>
            <div><label className="text-sm font-medium text-zinc-700">End (optional)</label><Input type="datetime-local" data-testid="visit-end" value={vEnd} onChange={(e) => setVEnd(e.target.value)} className="mt-1 h-11" /></div>
            <div><label className="text-sm font-medium text-zinc-700">Location instructions (optional)</label><Input data-testid="visit-loc" value={vLoc} onChange={(e) => setVLoc(e.target.value)} placeholder="e.g. Ask for Sam at the front desk" className="mt-1 h-11" maxLength={500} /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setVisitOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="visit-propose-submit" onClick={proposeVisit} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Propose visit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deadline dialog */}
      <Dialog open={deadlineOpen} onOpenChange={setDeadlineOpen}>
        <DialogContent className="max-w-md" data-testid="deadline-dialog">
          <DialogHeader><DialogTitle>Propose a content deadline</DialogTitle><DialogDescription>Pick the date the content should be posted by. The other participant will confirm.</DialogDescription></DialogHeader>
          <div><label className="text-sm font-medium text-zinc-700">Deadline date</label><Input type="date" data-testid="deadline-date" value={dDate} onChange={(e) => setDDate(e.target.value)} className="mt-1 h-11" /></div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeadlineOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="deadline-propose-submit" onClick={proposeDeadline} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Propose deadline"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
